import { useEffect, useRef } from 'react';

/**
 * Run U: two Save buttons share one config document, and each save refetches it. A form that resets
 * itself from every refetched config therefore ate the OTHER form's unsaved edits. The rule: apply the
 * stored values only when it cannot lose an edit.
 */
export function shouldApplyStored(input: {
  keyChanged: boolean;
  /** Snapshot of the stored values the form was last reset to (null before the first reset). */
  applied: string | null;
  /** Snapshot of the form right now. */
  form: string;
  /** Snapshot of the stored values now. */
  stored: string;
}): boolean {
  if (input.keyChanged || input.applied === null) return true;
  const dirty = input.form !== input.applied;
  // Clean, or the save the user just made (the stored values now equal the form): safe to re-apply.
  return !dirty || input.stored === input.form;
}

export function useGuardedReset(opts: { key: string; stored: string; form: string; apply: () => void }): {
  dirty: boolean;
} {
  const applied = useRef<string | null>(null);
  const lastKey = useRef<string | null>(null);
  const formRef = useRef(opts.form);
  formRef.current = opts.form;
  const applyRef = useRef(opts.apply);
  applyRef.current = opts.apply;
  useEffect(() => {
    if (
      shouldApplyStored({
        keyChanged: lastKey.current !== opts.key,
        applied: applied.current,
        form: formRef.current,
        stored: opts.stored,
      })
    ) {
      applyRef.current();
      applied.current = opts.stored;
      lastKey.current = opts.key;
    }
  }, [opts.key, opts.stored]);
  return { dirty: applied.current !== null && opts.form !== applied.current };
}
